import { useQuery } from "@tanstack/react-query";
import { customFetch } from "@workspace/api-client-react";

export interface PrintAgentDevice {
  deviceId: string;
  hostname: string | null;
  os: string | null;
  agentVersion: string | null;
  lastSeenAt: string | null;
  semImpressora: boolean;
}

// Aparelhos do app de impressão vistos nos últimos 7 dias. Compartilhado com o
// card "Impressora Térmica" (Settings), que esconde o USB do navegador quando a
// loja já imprime pelo app do Windows, e com o pop-up da tela inicial.
export function usePrintAgentDevices(opts: { enabled?: boolean; refetchInterval?: number | false } = {}) {
  return useQuery<PrintAgentDevice[]>({
    queryKey: ["print-agent-devices"],
    queryFn: async () => {
      const r = await customFetch<{ devices: PrintAgentDevice[] }>(
        "/api/settings/print-agent/devices",
      );
      return r.devices;
    },
    enabled: opts.enabled ?? true,
    refetchInterval: opts.refetchInterval ?? 60_000,
  });
}

// O app renova a sessão a cada ~15 min e é aí que o aparelho é "visto":
// 20 min de janela = app aberto agora.
export function deviceRecente(d: PrintAgentDevice, minutos = 20): boolean {
  if (!d.lastSeenAt) return false;
  return Date.now() - new Date(d.lastSeenAt).getTime() < minutos * 60_000;
}

export function versaoMenor(atual: string, ultima: string): boolean {
  const parse = (v: string) => v.replace(/^v/, "").split("-")[0]!.split(".").map((n) => parseInt(n, 10) || 0);
  const a = parse(atual);
  const b = parse(ultima);
  for (let i = 0; i < Math.max(a.length, b.length); i++) {
    if ((a[i] ?? 0) !== (b[i] ?? 0)) return (a[i] ?? 0) < (b[i] ?? 0);
  }
  return false;
}

// O build pra Windows 7, 8 e 8.1 sai só como prerelease "-win8" e tem link e
// versão próprios no Worker: o instalador normal não roda nesses sistemas.
export type BuildDoApp = "windows" | "win8";

export function buildDoAparelho(d: PrintAgentDevice): BuildDoApp {
  return /win8/i.test(d.agentVersion ?? "") ? "win8" : "windows";
}

export const LINK_DO_APP: Record<BuildDoApp, string> = {
  windows: "/download/print-agent",
  win8: "/download/print-agent/win8",
};

const VERSAO_DO_APP: Record<BuildDoApp, string> = {
  windows: "/download/print-agent/latest.json",
  win8: "/download/print-agent/win8.json",
};

// Versão mais nova de cada build (o Worker resolve a release do GitHub).
export function useUltimaVersaoDoApp(build: BuildDoApp, enabled = true) {
  return useQuery<{ version: string } | null>({
    queryKey: build === "win8" ? ["print-agent-latest-win8"] : ["print-agent-latest"],
    queryFn: async () => {
      const r = await fetch(VERSAO_DO_APP[build]);
      return r.ok ? ((await r.json()) as { version: string }) : null;
    },
    staleTime: 60 * 60_000,
    enabled,
  });
}
